import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject, Signal,
  signal,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {takeUntilDestroyed, toObservable, toSignal} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {combineLatest, merge, of} from 'rxjs';
import {catchError, distinctUntilChanged, map, startWith, switchMap} from 'rxjs/operators';
import {NgbOffcanvas} from '@ng-bootstrap/ng-bootstrap';
import {ListboxComponent, ListboxConfig, ListboxItem, TagItem} from '@groundworks/ngx-groundworks-ui';
import {BranchInfo, ProjectScheduleInfo} from '../../sdk/sdk';
import {BranchService} from '../../services/branch.service';
import {ProjectService, ProjectTypeMapping} from '../../services/project.service';
import {BranchHubService} from '../../services/branch-hub.service';
import {MultiBranchSelectorComponent} from '../multi-branch-selector/multi-branch-selector.component';
import {DateRangeSelectorComponent} from '../date-range-selector/date-range-selector.component';
import {MultiProjectTypeSelectorComponent} from '../multi-project-type-selector/multi-project-type-selector.component';
import {InvalidProjectTypeConstants} from '../../constants/invalid-project-type.constants';

const JOBS_CONFIG: ListboxConfig = {
  columns: [
    {header: 'Branch', field: 'branch', sortable: true},
    {header: 'Customer Name', field: 'customerName', sortable: true},
    {header: 'City', field: 'city', sortable: true},
    {header: 'Sold Date', field: 'soldDate', sortable: true, dataType: 'date'},
    {header: 'Project Type', field: 'projectType', sortable: true},
    {header: 'Project Total', field: 'projectTotal', sortable: true, dataType: 'currency'},
    {header: '', field: 'action', sortable: false},
  ],
};

function toListboxItem(project: ProjectScheduleInfo): ListboxItem {
  return {
    id: project.id,
    branch: project.branchName ?? '',
    customerName: getPrimaryContactName(project),
    city: project.address?.city ?? '',
    soldDate: project.soldAt,
    projectType: project.projectTypeName ?? '',
    projectTotal: project.projectNetAmount ?? 0,
    action: project.opportunityId,
  };
}

function formatDateRangeChip(from: Date | null, to: Date | null): string {
  const fmt = (d: Date) =>
    d.toLocaleDateString('en-US', {month: '2-digit', day: '2-digit', year: '2-digit'});
  if (from && to) return `${fmt(from)} - ${fmt(to)}`;
  if (from) return `From ${fmt(from)}`;
  return `To ${fmt(to!)}`;
}

@Component({
  selector: 'app-projects-listbox',
  imports: [ListboxComponent, MultiBranchSelectorComponent, DateRangeSelectorComponent, MultiProjectTypeSelectorComponent],
  templateUrl: './projects-listbox.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectsListboxComponent {
  @ViewChild('settingsDrawer') settingsDrawerTemplate!: TemplateRef<unknown>;

  private readonly router = inject(Router);
  private readonly branchService = inject(BranchService);
  private readonly projectService = inject(ProjectService);
  private readonly branchHubService = inject(BranchHubService);
  private readonly offcanvasService = inject(NgbOffcanvas);
  readonly config = JOBS_CONFIG;

  private readonly refreshTrigger = signal(0);
  readonly invalidProjectTypes = InvalidProjectTypeConstants
  readonly selectedBranch = toSignal(this.branchService.getSelectedBranch(), {initialValue: null});
  readonly selectedBranches = toSignal(this.branchService.getSelectedBranches(), {initialValue: []});

  readonly activeProjectTypes = signal<string[]>([]);
  readonly activeDateRange = signal<{ from: Date | null; to: Date | null }>({from: null, to: null});
  errorMessage = signal<string | null>(null);

  tabs = computed(() => {
    if (this.selectedBranches().length === 0 && this.selectedBranch() === null)
      return [];

    return [
      {
        id: 1,
        label: 'To-Do',
        config: this.config,
        items: this.unscheduledProjects()
      },
      {
        id: 2,
        label: 'Scheduled',
        config: this.config,
        items: this.recentlyScheduledProjects()
      }
    ];
  })

  activeTags: Signal<TagItem[]> = computed(() => {
    const dateRangeTags: Omit<TagItem, 'id'>[] =
      this.activeDateRange().from != null || this.activeDateRange().to != null
        ? [{label: this.activeDateRangeChip() ?? '', action: () => this.clearDateRangeFilter()}]
        : [];

    const branchTags: Omit<TagItem, 'id'>[] =
      this.selectedBranches().length > 0
        ? this.selectedBranches().map(b => ({
            label: b.fullBranchName || b.name!,
            action: () => this.removeBranchFilter(b.name!)
          }))
        : [];

    const typeTags: Omit<TagItem, 'id'>[] =
      this.activeProjectTypes().map(t => ({label: t, action: () => this.removeProjectTypeFilter(t)}));

    return [...dateRangeTags, ...branchTags, ...typeTags].map((t, i) => ({...t, id: i + 1}));
  });

  pendingSettings = {
    projectTypes: [] as string[],
    fromDate: null as Date | null,
    toDate: null as Date | null,
    branch: null as BranchInfo | null,
    branches: [] as BranchInfo[],
  };

  readonly noDataMessage = computed(() => {
    if (this.selectedBranches().length === 0 && this.selectedBranch() === null) {
      return 'Please select a branch';
    }
    return 'No records found';
  });

  private readonly branchIds$ = combineLatest([
    this.branchService.getSelectedBranches(),
    this.branchService.getSelectedBranch(),
  ]).pipe(
    map(([branches, singleBranch]) => {
      if (branches.length > 0) return branches.map(b => b.id).filter((id): id is string => !!id);
      if (singleBranch?.id) return [singleBranch.id];
      return [];
    }),
    distinctUntilChanged((a, b) => a.length === b.length && a.every((id, i) => id === b[i])),
  );

  private readonly projectsState = toSignal(
    combineLatest([
      this.branchIds$,
      toObservable(this.refreshTrigger),
    ]).pipe(
      map(([branchIds]) => branchIds),
      switchMap(branchIds => {
        this.errorMessage.set(null);
        if (branchIds.length === 0) return of({loading: false, projects: [] as ProjectScheduleInfo[]});
        return this.projectService.getProjects(branchIds).pipe(
          map(projects => ({loading: false, projects})),
          startWith({loading: true, projects: [] as ProjectScheduleInfo[]}),
          catchError(err => {
            console.error('Unable to load projects list', err);
            this.errorMessage.set('Failed to load projects');
            return of({loading: false, projects: [] as ProjectScheduleInfo[]});
          })
        );
      })
    ),
    {initialValue: {loading: false, projects: [] as ProjectScheduleInfo[]}}
  );

  constructor() {
    merge(
      this.branchHubService.projectScheduled$,
      this.branchHubService.projectUnscheduled$,
    ).pipe(
      takeUntilDestroyed(),
    ).subscribe(() => this.refreshTrigger.update(n => n + 1));
  }

  readonly isLoading = computed(() => this.projectsState().loading);
  private readonly allProjects = computed(() => this.projectsState().projects
    .filter((p) => !this.invalidProjectTypes.includes(p.projectTypeName ?? ''))
    .filter((p) => !p.isFullyDescoped && p.boundProducts ? p.boundProducts.length > 0 : false));
  readonly availableProjectTypes = toSignal(
    this.projectService.getProjectTypes().pipe(
      catchError(err => {
        console.error('Unable to load project types', err);
        this.errorMessage.set('Failed to load project types');
        return of([] as ProjectTypeMapping[]);
      })
    ),
    {initialValue: [] as ProjectTypeMapping[]}
  );
  readonly items = computed(() => this.filterProjectsBy('none'));
  readonly unscheduledProjects = computed(() => this.filterProjectsBy('todo'));
  readonly recentlyScheduledProjects = computed(() => this.filterProjectsBy('scheduled'));
  readonly activeDateRangeChip = computed(() => {
    const range = this.activeDateRange();
    return range.from || range.to ? formatDateRangeChip(range.from, range.to) : null;
  });
  readonly hasActiveFilters = computed(
    () => this.activeProjectTypes().length > 0 || !!this.activeDateRangeChip()
  );

  private filterProjectsBy(filter: 'none' | 'todo' | 'scheduled') {
    let projects = this.allProjects();
    const types = this.activeProjectTypes();
    const range = this.activeDateRange();

    if (filter === 'todo') {
      projects = projects.filter((p) =>
        (p.installDates === undefined || p.installDates.length === 0) && !p.completionDate
      );
    }
    if (filter === 'scheduled') {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      sevenDaysAgo.setHours(0, 0, 0, 0);

      projects = projects.filter((p) =>
        p.installDates != null &&
        p.installDates.length > 0 &&
        p.installDates.some((d) => new Date(d.createdAt) >= sevenDaysAgo)
      );
    }
    if (types.length > 0) {
      projects = projects.filter((p) => types.includes(p.projectTypeName ?? ''));
    }
    if (range.from) {
      projects = projects.filter((p) => p.soldAt >= range.from!);
    }
    if (range.to) {
      const endOfDay = new Date(range.to);
      endOfDay.setHours(23, 59, 59, 999);
      projects = projects.filter((p) => p.soldAt <= endOfDay);
    }
    return projects
      .sort((a, b) => a.soldAt.getTime() - b.soldAt.getTime() || this.filterByName(a, b))
      .map(toListboxItem);
  }

  private filterByName(first: ProjectScheduleInfo, second: ProjectScheduleInfo) {
    return getPrimaryContactName(first).localeCompare(getPrimaryContactName(second));
  }

  openSettings(): void {
    this.pendingSettings = {
      projectTypes: [...this.activeProjectTypes()],
      fromDate: this.activeDateRange().from,
      toDate: this.activeDateRange().to,
      branch: this.selectedBranch(),
      branches: this.selectedBranches(),
    };
    this.offcanvasService.open(this.settingsDrawerTemplate, {
      ariaLabelledBy: 'table-settings',
      position: 'end',
    });
  }

  applySettings(): void {
    const {projectTypes, fromDate, toDate, branch, branches} = this.pendingSettings;
    if (branches.length > 0) {
      this.branchService.setSelectedBranches(branches);
      this.branchService.setSelectedBranch(branches[0]);
    } else if (branch) {
      this.branchService.setSelectedBranch(branch);
    } else {
      this.branchService.setSelectedBranches([]);
      this.branchService.setSelectedBranch(null);
    }
    this.activeProjectTypes.set([...projectTypes]);
    this.activeDateRange.set({from: fromDate, to: toDate});
    this.offcanvasService.dismiss('Settings applied');
  }

  resetToDefault(): void {
    this.pendingSettings = {projectTypes: [], fromDate: null, toDate: null, branch: null, branches: []};
    this.activeProjectTypes.set([]);
    this.activeDateRange.set({from: null, to: null});
    this.branchService.setSelectedBranches([]);
    this.branchService.setSelectedBranch(null);
    this.offcanvasService.dismiss('Reset to default');
  }

  togglePendingProjectType(type: string): void {
    const types = this.pendingSettings.projectTypes;
    this.pendingSettings.projectTypes = types.includes(type)
      ? types.filter((t) => t !== type)
      : [...types, type];
  }

  isPendingProjectTypeSelected(type: string): boolean {
    return this.pendingSettings.projectTypes.includes(type);
  }

  removeProjectTypeFilter(type: string): void {
    this.activeProjectTypes.update((types) => types.filter((t) => t !== type));
  }

  removeBranchFilter(branchName: string): void {
    const updatedFilter = this.selectedBranches().filter((branch) =>
      branch.name !== branchName);
    this.branchService.setSelectedBranches(updatedFilter);
    if (updatedFilter.length > 0) {
      this.branchService.setSelectedBranch(updatedFilter[0]);
    } else {
      this.branchService.setSelectedBranch(null);
    }
  }

  clearDateRangeFilter(): void {
    this.activeDateRange.set({from: null, to: null});
  }

  clearAllFilters(): void {
    this.activeProjectTypes.set([]);
    this.activeDateRange.set({from: null, to: null});

  }

  clearAllTags = () => {
    this.clearAllFilters();
    this.branchService.setSelectedBranches([]);
    this.branchService.setSelectedBranch(null);
  }

  onPendingDateRangeChanged(range: { startDate?: Date; endDate?: Date }): void {
    this.pendingSettings.fromDate = range.startDate ?? null;
    this.pendingSettings.toDate = range.endDate ?? null;
  }

  onTypesSelected(types: ProjectTypeMapping[]): void {
    this.pendingSettings.projectTypes = types.map((projectType) => projectType.name);
  }

  onBranchesSelected(branches: BranchInfo[]): void {
    this.pendingSettings.branches = branches;
    this.pendingSettings.branch = branches[0] ?? null;
  }

  onJobClick(opportunityId: string): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/viewer'], {queryParams: {opportunityId}})
    );
    window.open(url, '_blank');
  }
}

function getPrimaryContactName(project: ProjectScheduleInfo): string {
  const contact = project.contacts?.find((c) => c.isPrimary) ?? project.contacts?.[0];
  return contact ? `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim() : '';
}
